import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VATRulesConsentPage } from "@eclear-ag/ec-common-ui/pricing";

/**
 * Design preview for the VATRules consent screen.
 *
 * The screen itself lives in ec-common-ui; this route only mounts it with
 * sample billing figures so the design can be seen and clicked through before
 * the real checkout flow is wired to it. Everything is driven by query params
 * so each plan and state can be checked without a rebuild:
 *
 *   /spot/manage-profile/consent-preview
 *   /spot/manage-profile/consent-preview?plan=professional
 *   /spot/manage-profile/consent-preview?plan=free
 *   /spot/manage-profile/consent-preview?existingPayment=false
 *   /spot/manage-profile/consent-preview?due=%E2%82%AC99&renewal=%E2%82%AC1188
 */

/** Sample figures per plan — placeholders only, until the backend supplies them. */
const SAMPLE_BILLING = {
  free: { dueToday: "€0", renewalPrice: "€0", renewalDate: "July 1, 2026" },
  starter: { dueToday: "€129", renewalPrice: "€1548", renewalDate: "July 1, 2026" },
  professional: { dueToday: "€249", renewalPrice: "€2988", renewalDate: "July 1, 2026" }
};

export default function ConsentPreviewRoute() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const plan = searchParams.get("plan") ?? "starter";
  const sample = SAMPLE_BILLING[plan] ?? SAMPLE_BILLING.starter;

  // The note is the interesting case to look at, so it is on unless switched
  // off — the opposite default to production, where it is the rarer branch.
  const existingPayment = searchParams.get("existingPayment") !== "false";

  // Rendered full screen via a portal, the same way the upgrade wizard escapes
  // the app shell (header/sidebar) — see upgrade-wizard-route.js for why the
  // fixed wrapper is required and not just the portal.
  return createPortal(
    <div style={{ position: "fixed", inset: 0, overflowY: "auto", zIndex: 1400 }}>
      <VATRulesConsentPage
        planId={plan}
        dueToday={searchParams.get("due") ?? sample.dueToday}
        renewalPrice={searchParams.get("renewal") ?? sample.renewalPrice}
        renewalDate={searchParams.get("renewalDate") ?? sample.renewalDate}
        alreadyPaysForAnEclearProduct={existingPayment}
        onBack={() => navigate("/spot/manage-profile?tab=subscriptions")}
        onConfirm={() =>
          // eslint-disable-next-line no-alert
          window.alert(`Consent given for the ${plan} plan (preview — nothing was charged).`)
        }
      />
    </div>,
    document.body
  );
}
